import { CheckIcon, PlusIcon } from '../components/icons'
import { JobIcon } from '../components/JobIcon'
import { formatMoney, normalizePrice } from '../lib/format'
import { matchJobIcon, type JobType } from '../lib/jobTypes'
import type { BasketItem } from '../lib/services'
import type { WidgetBootstrapResponse } from '../types/PublicBookingChannelTypes'

export interface TyreAttachStepProps {
  /** Everything the channel sells, minus what is already fully in the basket. */
  available: JobType[]
  basket: BasketItem[]
  onToggleService: (jobType: JobType, service: WidgetBootstrapResponse.ServiceItem) => void
  showPrices: boolean
}

interface Attachment {
  jobType: JobType
  service: WidgetBootstrapResponse.ServiceItem
  price: number | null
}

/**
 * What else to do while the wheels are off.
 *
 * The tyre channel's answer to "anything else?", and deliberately not the general
 * cross-sell list. Two things earn their place on this screen and nothing else
 * does:
 *
 * - **Alignment.** The single most relevant thing to sell with tyres, and the
 *   reason this is a Tyres *and Tracking* widget rather than a tyre shop.
 *   Misalignment ruins a new set within a few thousand miles, which is a reason a
 *   customer can act on rather than an upsell.
 * - **Free checks.** Brakes and suspension cost the garage nothing extra while
 *   the wheels are already off, and they are where the next job comes from.
 *
 * Free checks are one tap and default to **off**. Pre-ticking them would lift the
 * attach rate and fill workshop sheets with work nobody asked for, which the
 * garage then has to unpick on arrival.
 */
export function TyreAttachStep({
  available,
  basket,
  onToggleService,
  showPrices,
}: TyreAttachStepProps) {
  const inBasket = (service: WidgetBootstrapResponse.ServiceItem) =>
    basket.some((item) => item.serviceId === service.id)

  // One attachment per category — the cheapest bookable thing in it. This screen
  // is for saying yes, not for browsing a menu; anything more considered belongs
  // in the garage's full journey.
  const attachments: Attachment[] = available
    .flatMap((jobType) => {
      const service = jobType.bookable[0]
      if (!service) return []
      return [{ jobType, service, price: normalizePrice(service.price) }]
    })
    .filter((entry) => entry.price !== null || entry.jobType.bookable.length === 1)

  const free = attachments.filter((entry) => entry.price === 0)
  const alignment = attachments.filter(
    (entry) => matchJobIcon(entry.jobType.key) === 'wheels' && entry.price !== 0
  )
  const rest = attachments.filter(
    (entry) => !free.includes(entry) && !alignment.includes(entry)
  )

  if (attachments.length === 0) {
    return (
      <div className="ac-stack">
        <div className="ac-stack ac-stack--tight">
          <p className="ac-eyebrow">Nearly there</p>
          <h3 className="ac-heading">You&apos;re all set</h3>
          <p className="ac-prose">Pick a time that suits you next.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="ac-stack">
      <div className="ac-stack ac-stack--tight">
        <p className="ac-eyebrow">While the wheels are off</p>
        <h3 className="ac-heading">Worth doing at the same time</h3>
        <p className="ac-prose">
          No second visit, no extra strip-down, and the free checks cost nothing.
        </p>
      </div>

      {alignment.map(({ jobType, service, price }) => {
        const selected = inBasket(service)

        return (
          <button
            key={service.id}
            type="button"
            className={`ac-attach${selected ? ' ac-attach--selected' : ''}`}
            aria-pressed={selected}
            onClick={() => onToggleService(jobType, service)}
          >
            <span className="ac-attach__icon" aria-hidden="true">
              <JobIcon name={jobType.icon} size={24} />
            </span>
            <span className="ac-attach__main">
              <span className="ac-attach__title">{service.name}</span>
              <span className="ac-attach__detail">
                Misalignment scrubs a new set out within a few thousand miles.
              </span>
            </span>
            <span className="ac-attach__action">
              {selected ? (
                <CheckIcon size={18} />
              ) : showPrices && price != null ? (
                formatMoney(price)
              ) : (
                'Add'
              )}
            </span>
          </button>
        )
      })}

      {free.length > 0 ? (
        <div className="ac-stack ac-stack--tight">
          <h4 className="ac-section-label">Free while it&apos;s in</h4>
          <div className="ac-chips">
            {free.map(({ jobType, service }) => {
              const selected = inBasket(service)

              return (
                <button
                  key={service.id}
                  type="button"
                  className={`ac-chip ac-chip--action${selected ? ' ac-chip--on' : ''}`}
                  aria-pressed={selected}
                  onClick={() => onToggleService(jobType, service)}
                >
                  {selected ? <CheckIcon size={13} /> : <PlusIcon size={13} />}
                  {service.name}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}

      {rest.length > 0 ? (
        <div className="ac-stack ac-stack--tight">
          {/* Everything else the garage sells. Not "while the wheels are off" —
              an MOT has nothing to do with the tyres — but a customer already
              booking a visit is the cheapest person to sell a second job to. */}
          <h4 className="ac-section-label">Also available on the day</h4>
        <ul className="ac-list">
          {rest.map(({ jobType, service, price }) => {
            const selected = inBasket(service)

            return (
              <li key={service.id}>
                <button
                  type="button"
                  className={`ac-option${selected ? ' ac-option--selected' : ''}`}
                  aria-pressed={selected}
                  onClick={() => onToggleService(jobType, service)}
                >
                  <span className="ac-option__check" aria-hidden="true">
                    <CheckIcon size={15} />
                  </span>
                  <span className="ac-option__main">
                    <span className="ac-option__name">{service.name}</span>
                    {service.description ? (
                      <span className="ac-option__desc">{service.description}</span>
                    ) : null}
                  </span>
                  <span className="ac-option__meta">
                    {showPrices && price != null ? (
                      <span className="ac-option__price">
                        {price > 0 ? formatMoney(price) : 'Free'}
                      </span>
                    ) : null}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
        </div>
      ) : null}
    </div>
  )
}
